import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AppState } from '@app/store/state/app.state';
import { naturalSort } from '@shared/utils/ArrayUtils';
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import {
  CreateLockerRequest,
  Locker,
  LockerOfficeSummary,
  UpdateLockerRequest,
  WaitingList
} from './lockers.models';
import {
  assignLockerRequest,
  createLockerRequest,
  deleteLockerRequest,
  getLockerSummary,
  getLockersForOffice,
  getWaitingListRequest,
  joinWaitingListRequest,
  leaveWaitingListRequest,
  unassignLockerRequest,
  updateLockerRequest
} from './lockersService';

export interface LockersState {
  summary: LockerOfficeSummary[];
  lockers: Locker[];
  waitingList: WaitingList | null;
  selectedOfficeId: number | null;
  loading: boolean;
  saving: boolean;
}

export const initialLockersState: LockersState = {
  summary: [],
  lockers: [],
  waitingList: null,
  selectedOfficeId: null,
  loading: false,
  saving: false,
};

export const loadLockerSummary: any = createAsyncThunk(
  'lockers/loadSummary',
  async (_, thunkAPI) => {
    return await getLockerSummary();
  }
);

export const loadLockersForOffice: any = createAsyncThunk(
  'lockers/loadForOffice',
  async (officeId: number, thunkAPI) => {
    const [lockers, waitingList] = await Promise.all([
      getLockersForOffice(officeId),
      getWaitingListRequest(officeId),
    ]);
    return { lockers, waitingList };
  }
);

export const createLocker: any = createAsyncThunk(
  'lockers/create',
  async (request: CreateLockerRequest, thunkAPI) => {
    try {
      await createLockerRequest(request);
      thunkAPI.dispatch(successNotification('Locker created'));
      await refresh(thunkAPI);
    } catch (e) {
      thunkAPI.dispatch(failureNotification('Failed to create locker'));
      throw e;
    }
  }
);

export const updateLocker: any = createAsyncThunk(
  'lockers/update',
  async (params: { lockerId: number, request: UpdateLockerRequest }, thunkAPI) => {
    try {
      await updateLockerRequest(params.lockerId, params.request);
      thunkAPI.dispatch(successNotification('Locker updated'));
      await refresh(thunkAPI);
    } catch (e) {
      thunkAPI.dispatch(failureNotification('Failed to update locker'));
      throw e;
    }
  }
);

export const deleteLocker: any = createAsyncThunk(
  'lockers/delete',
  async (lockerId: number, thunkAPI) => {
    try {
      await deleteLockerRequest(lockerId);
      thunkAPI.dispatch(successNotification('Locker deleted'));
      await refresh(thunkAPI);
    } catch (e) {
      thunkAPI.dispatch(failureNotification('Failed to delete locker'));
      throw e;
    }
  }
);

export const assignLocker: any = createAsyncThunk(
  'lockers/assign',
  async (params: { lockerId: number, userId: string }, thunkAPI) => {
    try {
      await assignLockerRequest(params.lockerId, params.userId);
      thunkAPI.dispatch(successNotification('Locker assigned'));
      await refresh(thunkAPI);
    } catch (e) {
      thunkAPI.dispatch(failureNotification('Failed to assign locker'));
      throw e;
    }
  }
);

export const unassignLocker: any = createAsyncThunk(
  'lockers/unassign',
  async (lockerId: number, thunkAPI) => {
    try {
      await unassignLockerRequest(lockerId);
      thunkAPI.dispatch(successNotification('Locker released'));
      await refresh(thunkAPI);
    } catch (e) {
      thunkAPI.dispatch(failureNotification('Failed to release locker'));
      throw e;
    }
  }
);

export const joinWaitingList: any = createAsyncThunk(
  'lockers/joinWaitingList',
  async (params: { officeId: number, userId?: string }, thunkAPI) => {
    try {
      await joinWaitingListRequest(params.officeId, params.userId);
      thunkAPI.dispatch(successNotification('Added to the waiting list'));
      await refresh(thunkAPI);
    } catch (e) {
      thunkAPI.dispatch(failureNotification('Failed to join the waiting list'));
      throw e;
    }
  }
);

export const leaveWaitingList: any = createAsyncThunk(
  'lockers/leaveWaitingList',
  async (entryId: number, thunkAPI) => {
    try {
      await leaveWaitingListRequest(entryId);
      thunkAPI.dispatch(successNotification('Removed from the waiting list'));
      await refresh(thunkAPI);
    } catch (e) {
      thunkAPI.dispatch(failureNotification('Failed to leave the waiting list'));
      throw e;
    }
  }
);

/**
 * Every mutation changes both the office lists and the headline counts, so reload both rather
 * than patching state in two places and risking them disagreeing.
 */
async function refresh(thunkAPI: any) {
  const officeId = (thunkAPI.getState() as AppState).lockers.selectedOfficeId;
  thunkAPI.dispatch(loadLockerSummary());
  if (officeId) {
    await thunkAPI.dispatch(loadLockersForOffice(officeId));
  }
}

const lockersSlice = createSlice({
  name: 'lockers',
  initialState: initialLockersState,
  reducers: {
    resetLockers: () => initialLockersState,
    setSelectedLockerOffice: (state, action) => ({ ...state, selectedOfficeId: action.payload }),
  },
  extraReducers: (builder) => {
    builder.addCase(loadLockerSummary.fulfilled, (state, action) => {
      state.summary = action.payload;
    });

    builder.addCase(loadLockersForOffice.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loadLockersForOffice.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(loadLockersForOffice.fulfilled, (state, action) => {
      state.loading = false;
      state.lockers = action.payload.lockers;
      state.waitingList = action.payload.waitingList;
    });

    [createLocker, updateLocker, deleteLocker, assignLocker, unassignLocker, joinWaitingList, leaveWaitingList]
      .forEach((thunk) => {
        builder.addCase(thunk.pending, (state) => {
          state.saving = true;
        });
        builder.addCase(thunk.rejected, (state) => {
          state.saving = false;
        });
        builder.addCase(thunk.fulfilled, (state) => {
          state.saving = false;
        });
      });
  },
});

export default lockersSlice.reducer;
export const { resetLockers, setSelectedLockerOffice } = lockersSlice.actions;

// Selectors
export const selectLockerSummary = (state: AppState) => state.lockers.summary;
export const selectLockers = (state: AppState) => state.lockers.lockers;
// filter() already returns a fresh array, so sorting it in place cannot touch store state.
// naturalSort keeps "Locker 2" ahead of "Locker 10" rather than ordering them as strings.
export const selectAvailableLockers = (state: AppState) =>
  naturalSort(state.lockers.lockers.filter((l: Locker) => l.active && !l.assigned), 'label');
export const selectAssignedLockers = (state: AppState) =>
  naturalSort(state.lockers.lockers.filter((l: Locker) => l.assigned), 'label');
// The admin table is one list, not two panels, so available and assigned share a single ordering.
// Retired lockers still show while they hold someone's things, which is why this is not just
// available + assigned concatenated.
export const selectAllLockersForOffice = (state: AppState) =>
  naturalSort(state.lockers.lockers.filter((l: Locker) => l.active || l.assigned), 'label');
export const selectLockerWaitingList = (state: AppState) => state.lockers.waitingList;
export const selectSelectedLockerOfficeId = (state: AppState) => state.lockers.selectedOfficeId;
export const selectIsLockersLoading = (state: AppState) => state.lockers.loading;
export const selectIsLockersSaving = (state: AppState) => state.lockers.saving;
